import { useState } from 'react';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { openTab, type TabKind } from '../state/tabs.ts';
import { Icon, type IconName } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { RecentActions } from './RecentActions.tsx';

// Het vel Meer op de telefoon: de functies die niet in de onderste navigatie passen, de overzichten
// en de instellingen van Jerome, plus uitloggen. Recente acties opent als vel bovenop dit vel.

interface Row {
  id: string;
  label: string;
  icon: IconName;
  hint?: string | undefined;
  tone?: 'amber' | undefined;
  run: () => void;
}

export function MoreSheet(props: { onClose: () => void }) {
  const me = useStore(app, (s) => s.me);
  const version = useStore(app, (s) => s.serverVersion ?? s.me?.version ?? null);
  const newVersion = useStore(app, (s) => s.newVersion);
  const workbooks = useStore(app, (s) => s.settings.workbooks);
  const [recent, setRecent] = useState(false);
  const go = (kind: TabKind, opts: { title?: string; state?: Record<string, unknown> } = {}) => {
    openTab(kind, { single: true, ...opts });
    props.onClose();
  };
  const sections: Array<{ title: string; rows: Row[] }> = [
    {
      title: 'Functies',
      rows: [
        { id: 'rules', label: 'Regels', icon: 'sliders', run: () => go('rules') },
        { id: 'dev', label: 'Ontwikkelen', icon: 'code', run: () => go('dev') },
        ...workbooks.map((wb): Row => ({ id: `wb-${wb.id}`, label: wb.name, icon: 'table', hint: 'Excel', run: () => go('excel', { title: wb.name, state: { workbookId: wb.id } }) })),
      ],
    },
    {
      title: 'Overzichten',
      rows: [
        { id: 'recent', label: 'Recente acties', icon: 'clock', hint: 'ongedaan maken', run: () => setRecent(true) },
        { id: 'briefing', label: 'Samenvattingen', icon: 'sun', run: () => go('briefing') },
        { id: 'usage', label: 'Gebruik', icon: 'chart', run: () => go('usage') },
      ],
    },
    {
      title: 'Jerome',
      rows: [
        { id: 'settings', label: 'Instellingen', icon: 'settings', run: () => go('settings') },
        { id: 'knowledge', label: 'Kennis', icon: 'book', run: () => go('knowledge') },
        { id: 'releases', label: 'Versie en wensen', icon: 'info', hint: version ? `v${version}` : undefined, run: () => go('releases') },
        ...(newVersion ? [{ id: 'reload', label: 'Nieuwe versie, herlaad', icon: 'refresh' as const, tone: 'amber' as const, run: () => location.reload() }] : []),
        { id: 'logout', label: 'Uitloggen', icon: 'logout', run: () => void fetch('/auth/logout', { method: 'POST' }).then(() => location.reload()) },
      ],
    },
  ];
  return (
    <>
      <Sheet title="Meer" onClose={props.onClose}>
        {me ? (
          <div className="px-5 pt-3 text-[13px] text-muted truncate">
            {me.name} · {me.email}
          </div>
        ) : null}
        {sections.map((s) => (
          <section key={s.title} aria-label={s.title} className="px-3 pt-3 last:pb-3">
            <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">{s.title}</div>
            <ul className="rounded-xl bg-paper overflow-hidden">
              {s.rows.map((r) => (
                <li key={r.id} className="border-b border-line last:border-b-0">
                  {/* De naam is alleen het label; de hint (versie, "ongedaan maken") is bijzaak. */}
                  <button type="button" aria-label={r.label} onClick={r.run} className={`w-full h-12 flex items-center gap-3 px-3 text-left text-[15px] active:bg-tint ${r.tone === 'amber' ? 'text-amber' : 'text-ink'}`}>
                    <Icon name={r.icon} size={20} className={r.tone === 'amber' ? 'text-amber' : 'text-ink-soft'} />
                    <span className="flex-1 truncate">{r.label}</span>
                    {r.hint ? <span className="text-[13px] text-muted">{r.hint}</span> : null}
                    <Icon name="chevron" size={16} className="text-muted" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </Sheet>
      {recent ? (
        <Sheet title="Recente acties" height="full" onClose={() => setRecent(false)}>
          <RecentActions hideHeader />
        </Sheet>
      ) : null}
    </>
  );
}
